/**
 * Global odds-format preference (cents / implied % / American), persisted to
 * localStorage. Every price cell reads `useOddsFormat()` and renders via
 * `fmtPrice(...)`, so the top-bar toggle changes the whole grid at once.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import type { OddsFormat } from "./format.js";

const KEY = "gb.oddsFormat";
const VALID: OddsFormat[] = ["cents", "percent", "american"];

interface OddsCtx {
  format: OddsFormat;
  setFormat: (f: OddsFormat) => void;
}

const Ctx = createContext<OddsCtx>({ format: "cents", setFormat: () => {} });

function readInitial(): OddsFormat {
  if (typeof localStorage === "undefined") return "cents";
  const v = localStorage.getItem(KEY);
  return v && (VALID as string[]).includes(v) ? (v as OddsFormat) : "cents";
}

export function OddsFormatProvider({ children }: { children: ReactNode }) {
  const [format, setFormatState] = useState<OddsFormat>(readInitial);

  const setFormat = useCallback((f: OddsFormat) => {
    setFormatState(f);
    try {
      localStorage.setItem(KEY, f);
    } catch {
      /* private mode / quota — non-fatal */
    }
  }, []);

  // Sync across tabs.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === KEY && e.newValue && (VALID as string[]).includes(e.newValue)) {
        setFormatState(e.newValue as OddsFormat);
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const value = useMemo(() => ({ format, setFormat }), [format, setFormat]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useOddsFormat(): OddsCtx {
  return useContext(Ctx);
}

export const ODDS_FORMATS: { value: OddsFormat; label: string }[] = [
  { value: "cents", label: "¢" },
  { value: "percent", label: "%" },
  { value: "american", label: "±" },
];
